/**
 * What a database token is allowed to do, and how a checkbox changes it.
 *
 * The backend stores one value per operation, and that value is a blanket or a
 * list: `true` is every table in the workspace *including ones not made yet*,
 * `false` (or an empty list) is none, and a list of `["database", id]` and
 * `["table", id]` pairs is the tables those entries name. A database entry is
 * not shorthand for its tables -- it keeps covering the database as tables are
 * added to it -- so narrowing a blanket has to write the blanket out rather
 * than expand it to leaves.
 *
 * Pure, and separate from the section that renders it, because that rewriting
 * is the whole substance of the feature.
 */
import type {
  Application,
  DatabaseApplication,
  DatabaseTokenPermissions,
} from '@/api/types'

export const TOKEN_OPERATIONS = ['create', 'read', 'update', 'delete'] as const

export type TokenOperation = (typeof TOKEN_OPERATIONS)[number]

/** One operation's value: a blanket answer, or the list of what it covers. */
export type TokenScope = NonNullable<DatabaseTokenPermissions[TokenOperation]>

/** A `["database", 7]` or `["table", 7]` pair, as the schema types it. */
type Entry = (string | number)[]

/** Whether a scope covers everything, nothing, or some of it. */
export type Coverage = 'all' | 'none' | 'some'

const entriesOf = (scope: TokenScope | undefined): Entry[] =>
  Array.isArray(scope) ? scope : []

const holds = (entries: Entry[], type: 'database' | 'table', id: number): boolean =>
  entries.some((entry) => entry[0] === type && entry[1] === id)

const without = (entries: Entry[], type: 'database' | 'table', id: number): Entry[] =>
  entries.filter((entry) => !(entry[0] === type && entry[1] === id))

const scopeOf = (
  permissions: DatabaseTokenPermissions,
  operation: TokenOperation,
): TokenScope => permissions[operation] ?? false

/**
 * A blanket written out as one entry per database.
 *
 * Singling out a database or a table means the operation is no longer answered
 * for the workspace, so every database that was covered has to start saying so
 * for itself -- otherwise switching one table off would switch the rest off too.
 */
function expand(scope: TokenScope, databases: DatabaseApplication[]): Entry[] {
  if (scope === true) return databases.map((database) => ['database', database.id])
  return entriesOf(scope)
}

/** Whether the operation is allowed on every table in the workspace. */
export function workspaceCoverage(scope: TokenScope): Coverage {
  if (scope === true) return 'all'
  if (scope === false || entriesOf(scope).length === 0) return 'none'
  return 'some'
}

export function databaseCoverage(
  scope: TokenScope,
  database: DatabaseApplication,
): Coverage {
  if (scope === true) return 'all'
  const entries = entriesOf(scope)
  if (holds(entries, 'database', database.id)) return 'all'
  const some = database.tables.some((table) => holds(entries, 'table', table.id))
  return some ? 'some' : 'none'
}

export function tableCovered(
  scope: TokenScope,
  database: DatabaseApplication,
  tableId: number,
): boolean {
  if (scope === true) return true
  const entries = entriesOf(scope)
  return holds(entries, 'database', database.id) || holds(entries, 'table', tableId)
}

const replace = (
  permissions: DatabaseTokenPermissions,
  operation: TokenOperation,
  scope: TokenScope,
): DatabaseTokenPermissions => ({ ...permissions, [operation]: scope })

/** The workspace switch: a blanket either way, which is what the backend stores. */
export function setWorkspace(
  permissions: DatabaseTokenPermissions,
  operation: TokenOperation,
  on: boolean,
): DatabaseTokenPermissions {
  return replace(permissions, operation, on)
}

export function setDatabase(
  permissions: DatabaseTokenPermissions,
  operation: TokenOperation,
  databases: DatabaseApplication[],
  database: DatabaseApplication,
  on: boolean,
): DatabaseTokenPermissions {
  let entries = expand(scopeOf(permissions, operation), databases)

  // The database entry answers for every table under it, so the table entries
  // it supersedes go -- switching the database off must not leave one behind.
  for (const table of database.tables) entries = without(entries, 'table', table.id)
  entries = without(entries, 'database', database.id)
  if (on) entries = [...entries, ['database', database.id]]

  return replace(permissions, operation, entries)
}

export function setTable(
  permissions: DatabaseTokenPermissions,
  operation: TokenOperation,
  databases: DatabaseApplication[],
  database: DatabaseApplication,
  tableId: number,
  on: boolean,
): DatabaseTokenPermissions {
  let entries = expand(scopeOf(permissions, operation), databases)

  // Same reason one level down: the database is about to stop answering for its
  // tables, so the ones it covered are written out before it goes.
  if (holds(entries, 'database', database.id)) {
    for (const table of database.tables) {
      if (!holds(entries, 'table', table.id)) entries = [...entries, ['table', table.id]]
    }
    entries = without(entries, 'database', database.id)
  }

  entries = without(entries, 'table', tableId)
  if (on) entries = [...entries, ['table', tableId]]

  return replace(permissions, operation, entries)
}

/**
 * Entries naming a database or table that is no longer there.
 *
 * A database deleted in another tab leaves a permission pointing at nothing,
 * and the endpoint rejects the whole update rather than ignoring the entry --
 * so a token would become unsavable until the page was reloaded.
 */
export function prune(
  permissions: DatabaseTokenPermissions,
  databases: DatabaseApplication[],
): DatabaseTokenPermissions {
  const databaseIds = new Set(databases.map((database) => database.id))
  const tableIds = new Set(
    databases.flatMap((database) => database.tables.map((table) => table.id)),
  )

  let pruned = permissions
  for (const operation of TOKEN_OPERATIONS) {
    const scope = scopeOf(permissions, operation)
    if (!Array.isArray(scope)) continue
    const kept = scope.filter((entry) =>
      entry[0] === 'database'
        ? databaseIds.has(entry[1] as number)
        : tableIds.has(entry[1] as number),
    )
    if (kept.length !== scope.length) pruned = replace(pruned, operation, kept)
  }
  return pruned
}

/** The databases of a workspace, which is all a token's permissions can name. */
export function databasesOf(
  applications: Application[] | undefined,
): DatabaseApplication[] {
  return (applications ?? []).filter(
    (application): application is DatabaseApplication => application.type === 'database',
  )
}
